import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Divider,
  Input,
  Button,
  Form,
  notification,
  Space,
} from "antd";
import { ref as dbRef, update } from "firebase/database";
import { rtdb } from "../../../../firebase.config";
import { UserContext } from "../../../../context/UserContext";

const { Title, Text } = Typography;

const isPhoneValid = (phone) => {
  if (!phone) return false;

  const trimmed = phone.trim();

  if (/[^+\d\s]/.test(trimmed)) return false;

  if (!trimmed.startsWith("+")) return false;

  const digitsOnly = trimmed.replace(/\D/g, "");
  if (digitsOnly.length < 10 || digitsOnly.length > 15) return false;

  return true;
};

export default function PhoneChangePage() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      api.error({
        message: "Not signed in",
        description: "Please sign in to change your phone number.",
      });
      navigate("/landlord/settings");
    }
  }, [user?.uid, api, navigate]);

  const handleSave = async () => {
    try {
      const raw = form.getFieldValue("phone");
      if (!isPhoneValid(raw)) {
        api.error({
          message: "Invalid phone number",
          description:
            "Enter a valid international phone number starting with +",
        });
        return;
      }

      setSaving(true);
      await update(dbRef(rtdb, `landlords/${user.uid}`), {
        phone: raw.trim(),
      });

      api.success({
        message: "Phone updated",
        description:
          "Your phone number was saved. Verify it from the Settings page if required.",
      });

      navigate("/landlord/settings");
    } catch (err) {
      api.error({
        message: "Failed to update phone",
        description: err?.message || String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 520 }}>
      {contextHolder}
      <Title level={3}>Change Phone Number</Title>
      <Text type="secondary">
        Update your phone number. You can verify it from the Settings page if
        verification is required.
      </Text>

      <Divider />

      <Form form={form} layout="vertical" initialValues={{ phone: "" }}>
        <Form.Item
          label="New phone number"
          name="phone"
          rules={[
            { required: true, message: "Phone number is required" },
            {
              validator: (_, value) =>
                !value || isPhoneValid(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        "Enter a valid phone number (must start with + and be 10–15 digits)"
                      )
                    ),
            },
          ]}
        >
          <Input placeholder="+1 415 555 2671" />
        </Form.Item>

        <Row justify="end">
          <Col>
            <Space>
              <Button onClick={() => navigate("/landlord/settings")}>
                Cancel
              </Button>
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
